import { useState, useRef, useEffect } from "react";
import { usePlayer } from "@/contexts/PlayerContext";
import { useAlarm } from "@/contexts/AlarmContext";
import AlarmModal from "@/components/AlarmModal";
import { Play, Pause, Stop, SpeakerHigh, SpeakerLow, ArrowsOut, Moon, Alarm, MusicNotes } from "@phosphor-icons/react";
import { TEST_IDS } from "@/constants/testIds";

const SLEEP_OPTIONS = [
  { label: "Off", minutes: 0 },
  { label: "15 min", minutes: 15 },
  { label: "30 min", minutes: 30 },
  { label: "45 min", minutes: 45 },
  { label: "60 min", minutes: 60 },
  { label: "90 min", minutes: 90 },
];

function fmtTime(s) {
  if (!s || !Number.isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export default function PlayerBar() {
  const {
    current, mode, isPlaying, toggle, stop, volume, setVolume,
    setExpanded, sleepMinutes, sleepEndsAt, setSleepTimer,
    progress, duration, seek, backgroundAudioReady,
  } = usePlayer();
  const { alarms } = useAlarm();
  const [alarmOpen, setAlarmOpen] = useState(false);
  const [sleepOpen, setSleepOpen] = useState(false);
  const [countdown, setCountdown] = useState("");
  const dropRef = useRef(null);

  useEffect(() => {
    if (!sleepEndsAt) { setCountdown(""); return; }
    const tick = () => {
      const ms = sleepEndsAt - Date.now();
      if (ms <= 0) { setCountdown(""); return; }
      const m = Math.floor(ms / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setCountdown(`${m}:${s.toString().padStart(2, "0")}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sleepEndsAt]);

  useEffect(() => {
    const close = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setSleepOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  if (!current) return null;
  const isVideo = mode === "video";
  const isTrack = mode === "track";
  const isYTAudio = mode === "yt-audio";
  const isMP3orYT = isTrack || isYTAudio;
  const hasAlarm = (alarms || []).some(a => a.enabled);

  const title = isVideo || isTrack || isYTAudio ? current.title : current.name;
  const subtitle = isVideo
    ? current.channel
    : isTrack
      ? `${current.artist || ""}${current.album ? " · " + current.album : ""}`
      : isYTAudio
        ? `${current.channel || ""} · YouTube`
        : `${current.country || "—"} · ${(current.tags || "").split(",")[0] || "radio"}`;

  const showSeek = isMP3orYT && duration > 0;

  return (
    <div
      data-testid={TEST_IDS.player.bar}
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      className="fixed left-0 right-0 bottom-[68px] md:bottom-0 z-50 border-t border-[#ff4d1d]/40 bg-[#05070d]/90 text-white backdrop-blur-2xl shadow-[0_-22px_80px_rgba(0,0,0,.55)]"
    >
      {showSeek && (
        <div className="px-3 pt-2 flex items-center gap-2 max-w-[1440px] mx-auto">
          <span className="font-mono text-[9px] text-slate-400 w-8 text-right tabular-nums">{fmtTime(progress)}</span>
          <input
            data-testid="track-progress"
            type="range"
            min="0"
            max={duration}
            step="1"
            value={Math.min(progress, duration)}
            onChange={(e) => seek(parseFloat(e.target.value))}
            className="flex-1 accent-[#ff4d1d] h-1"
          />
          <span className="font-mono text-[9px] text-slate-400 w-8 tabular-nums">{fmtTime(duration)}</span>
        </div>
      )}

      <div className="max-w-[1440px] mx-auto px-3 sm:px-6 py-2.5 sm:py-4 flex items-center gap-2 sm:gap-4">
        {(isTrack || isVideo) && (
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl border border-white/10 shrink-0 bg-[#07111d] overflow-hidden shadow-[0_0_26px_rgba(255,77,29,.12)]">
            {isTrack && current.artwork ? (
              <img src={current.artwork} alt="" className="w-full h-full object-cover" />
            ) : isVideo && current.thumbnail ? (
              <img src={current.thumbnail} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center recho-fallback-art"><MusicNotes size={18} color="#09e7ff" weight="duotone" /></div>
            )}
          </div>
        )}

        {isPlaying && !isVideo && !isTrack && (
          <div className="hidden sm:flex items-end gap-[4px] h-10 w-10 shrink-0">
            {[0,1,2,3,4].map(i => (
              <div key={i} className="freq-bar w-1.5" style={{ height: "100%" }} />
            ))}
          </div>
        )}

        <button onClick={() => setExpanded(true)} className="flex-1 min-w-0 text-left group" data-testid={TEST_IDS.player.expand} aria-label="Expand player">
          <div className="font-serif-display text-base sm:text-2xl font-semibold truncate group-hover:text-[#ff7a18] transition-colors">{title}</div>
          <div className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.16em] text-slate-400 truncate">{subtitle}</div>
          <div className="hidden sm:flex items-center gap-2 mt-1 font-mono text-[8px] uppercase tracking-[0.18em] text-slate-500">
            <span className={`w-1.5 h-1.5 rounded-full ${isVideo ? "bg-amber-400" : backgroundAudioReady ? "bg-emerald-400" : "bg-slate-500"}`} />
            {isVideo ? "Video foreground only" : "Background + lock-screen audio ready"}
          </div>
        </button>

        {!isVideo && (
          <button
            data-testid={isPlaying ? TEST_IDS.player.pause : TEST_IDS.player.play}
            onClick={toggle}
            className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-[#ff4d1d]/10 border border-[#ff4d1d]/70 text-white hover:bg-[#ff4d1d] flex items-center justify-center transition-colors shrink-0 shadow-[0_0_30px_rgba(255,77,29,.28)]"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? <Pause size={20} weight="fill" /> : <Play size={20} weight="fill" />}
          </button>
        )}

        <button
          data-testid={TEST_IDS.player.stop}
          onClick={stop}
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-white/10 text-slate-300/100 hover:text-white hover:border-white/30 flex items-center justify-center shrink-0 transition-colors"
          aria-label="Stop"
        >
          <Stop size={15} weight="fill" />
        </button>

        {!isVideo && (
          <div className="relative hidden sm:block" ref={dropRef}>
            <button
              data-testid={TEST_IDS.player.sleep}
              onClick={() => setSleepOpen(o => !o)}
              className={`w-11 h-11 rounded-full border ${sleepMinutes ? "border-[#ff4d1d]/70 text-[#ff7a18] bg-[#ff4d1d]/10" : "border-white/10 text-slate-300/75 hover:border-white/30"} flex items-center justify-center relative transition-colors`}
              aria-label="Sleep timer"
            >
              <Moon size={16} weight={sleepMinutes ? "fill" : "regular"} />
              {countdown && <span className="absolute -top-1 -right-1 rounded-full bg-[#ff4d1d] text-white text-[8px] font-mono px-1.5">{countdown.split(":")[0]}m</span>}
            </button>
            {sleepOpen && (
              <div data-testid="sleep-timer-menu" className="absolute bottom-full right-0 mb-2 glass-card-strong overflow-hidden min-w-[160px]">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff7a18] px-3 py-2 border-b border-white/10">Sleep timer</div>
                {SLEEP_OPTIONS.map(opt => (
                  <button key={opt.minutes} data-testid={`sleep-option-${opt.minutes}`} onClick={() => { setSleepTimer(opt.minutes); setSleepOpen(false); }} className={`block w-full text-left px-3 py-2 font-sans text-sm hover:bg-[#ff4d1d] hover:text-white ${sleepMinutes === opt.minutes ? "text-[#ff7a18]" : "text-slate-200/100"}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {!isVideo && (
          <button
            data-testid={TEST_IDS.player.alarm}
            onClick={() => setAlarmOpen(true)}
            className={`hidden sm:flex w-11 h-11 rounded-full border ${hasAlarm ? "border-[#ff4d1d]/70 text-[#ff7a18] bg-[#ff4d1d]/10" : "border-white/10 text-slate-300/75 hover:border-white/30"} items-center justify-center transition-colors`}
            aria-label="Set alarm"
          >
            <Alarm size={16} weight={hasAlarm ? "fill" : "regular"} />
          </button>
        )}

        <button onClick={() => setExpanded(true)} className="hidden sm:flex w-11 h-11 rounded-full border border-white/10 text-slate-300/75 hover:text-white hover:border-white/30 items-center justify-center transition-colors" data-testid={`${TEST_IDS.player.expand}-btn`} aria-label="Expand player">
          <ArrowsOut size={16} />
        </button>

        {!isVideo && (
          <div className="hidden lg:flex items-center gap-2 min-w-[150px] text-slate-300/100">
            {volume > 0.4 ? <SpeakerHigh size={16} /> : <SpeakerLow size={16} />}
            <input data-testid={TEST_IDS.player.volume} type="range" min="0" max="1" step="0.05" value={volume} onChange={(e) => setVolume(parseFloat(e.target.value))} className="w-24 accent-[#ff4d1d]" />
          </div>
        )}
      </div>
      {alarmOpen && <AlarmModal onClose={() => setAlarmOpen(false)} />}
    </div>
  );
}
